import { Account } from '@/domain/usecases'
import { RouteObject, ShouldRevalidateFunctionArgs } from 'react-router-dom'

// Componentes de layout
import BasicLayout from '@/presentation/components/ui/layout/basic-layout'
import LoggedLayout from '@/presentation/components/ui/layout/logged-layout'

// Modelos de domínio para rotas dinâmicas
import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'

import { RecaptchaProvider } from '@/presentation/contexts/recaptcha/recaptcha-provider'

import { FeatureFlagName } from '@/infra/feature-flag'

import * as Loaders from '../../data-loaders'
import { createLoaderFactory, LoaderContext } from '../../shared/loaders'
import { PathConstants } from '../../paths'
import * as Pages from './pages'
import { userGuards } from './guards'

// ============================================
// ROTAS DO MÓDULO USER
// Fluxos normais da aplicação para perfis colaborador / dependente.
// ============================================

/**
 * Todas as rotas autenticadas de usuário (home, descoberta de conteúdo,
 * onboarding, perfil, inscrições, solicitações e rotas de ação).
 */
export const UserRoutes = (account: Account.Model): RouteObject[] => {
  const routeLoader = createLoaderFactory(account)

  return [
    // ========================================
    // ROTA HOME e LAYOUT LOGADO INICIAL
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="full" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.HOME.value,
          id: PathConstants.Routes.HOME.name,
          element: <Pages.HomePage />,
          loader: routeLoader(userGuards.passPlanHolder, () => Loaders.loadHome())
        }
      ]
    },

    // ========================================
    // ONBOARDING ROUTES
    // ========================================
    {
      element: <Pages.OnboardingLayout />,
      loader: routeLoader(userGuards.onboardingAccess, () => Loaders.loadOnboardingMainLayout()),
      children: [
        {
          path: PathConstants.Routes.ONBOARDING.value,
          element: <Pages.OnboardingWelcomePage />,
          loader: routeLoader(userGuards.onboardingAccess, null)
        },
        {
          path: PathConstants.Routes.ONBOARDING_PERSONAL_DATA.value,
          element: <Pages.OnboardingPersonalDataPage />,
          loader: routeLoader(userGuards.onboardingAccess, () =>
            Loaders.loadOnboardingStepOptions(1)
          )
        },
        {
          path: PathConstants.Routes.ONBOARDING_PROFESSIONAL_TRAJECTORY.value,
          element: <Pages.OnboardingProfessionalTrajectoryPage />,
          loader: routeLoader(userGuards.onboardingAccess, () =>
            Loaders.loadOnboardingStepOptions(1)
          )
        },
        {
          path: PathConstants.Routes.ONBOARDING_3.value,
          element: <Pages.Onboarding3Page />,
          loader: routeLoader(userGuards.onboardingAccess, () =>
            Loaders.loadOnboardingStepOptions(3)
          )
        },
        {
          path: PathConstants.Routes.ONBOARDING_4.value,
          element: <Pages.Onboarding4Page />,
          loader: routeLoader(userGuards.onboardingAccess, () => Loaders.loadOnboardingStep4())
        },
        {
          path: PathConstants.Routes.ONBOARDING_SECURITY.value,
          element: <Pages.OnboardingSecurityPage />,
          loader: routeLoader(userGuards.onboardingAccess, null)
        },
        {
          path: PathConstants.Routes.ONBOARDING_COMPLETED.value,
          element: <Pages.OnboardingCompletedPage />,
          loader: routeLoader(userGuards.onboardingAccess, null)
        }
      ]
    },

    // ========================================
    // ROTAS DE PERFIL DO USUÁRIO
    // Layout: LoggedLayout (navMenu="main", footerVariant="compact", enableSearch=true)
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="compact" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.USER_PROFILE.value,
          element: <Pages.UserProfilePage />,
          loader: routeLoader(userGuards.passPlanHolder, () => Loaders.loadUserProfile(account))
        },
        {
          path: PathConstants.Routes.USER_INSTEREST_AREAS_SKILLS.value,
          element: <Pages.UserInterestAreasSkillsPage />,
          loader: routeLoader(userGuards.passPlanHolder, () =>
            Loaders.loadUserInterestAreasSkills()
          )
        },
        {
          path: PathConstants.Routes.USER_SETTINGS.value,
          element: (
            <RecaptchaProvider>
              <Pages.UserSettingsPage />
            </RecaptchaProvider>
          ),
          loader: routeLoader(userGuards.passPlanHolder, null)
        },
        {
          path: PathConstants.Routes.USER_SPACE.value,
          element: <Pages.UserSpacePage />,
          loader: routeLoader(null, () => Loaders.loadUserSpace(account))
        },
        {
          path: PathConstants.Routes.USER_ENROLLMENTS_V2.value,
          element: <Pages.UserEnrollmentsPage />,
          loader: routeLoader(null, () => Loaders.loadUserEnrollments())
        },
        {
          path: PathConstants.Routes.USER_ENROLLMENT_DETAIL.value,
          element: <Pages.UserEnrollmentDetailPage />,
          loader: routeLoader(null, ({ params }: LoaderContext) =>
            Loaders.loadUserEnrollmentDetail(params!.enrollmentId!)
          )
        },
        {
          path: PathConstants.Routes.FAVORITES.value,
          id: PathConstants.Routes.FAVORITES.name,
          element: <Pages.FavoritesPage />,
          loader: routeLoader(null, null)
        }
      ]
    },

    // ========================================
    // ROTA DE ENGAJAMENTO DA INSCRIÇÃO DO USUÁRIO
    // Layout: BasicLayout (header com logo + seletor de idioma)
    // ========================================
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          path: PathConstants.Routes.USER_ENROLLMENT_ENGAGEMENT.value,
          element: <Pages.UserEnrollmentEngagementPage />,
          loader: routeLoader(null, ({ params, request }: LoaderContext) =>
            Loaders.loadUserEnrollmentEngagement(params!.enrollmentId!, request)
          )
        },
        {
          path: PathConstants.Routes.CONTENT_FIT_SCORE.value,
          id: PathConstants.Routes.CONTENT_FIT_SCORE.name,
          element: <Pages.ContentFitScorePage />,
          // A busca entra na comparação porque o `?offerId=` decide qual fit o loader pede:
          // reentrar na mesma rota com outra oferta precisa refazer a chamada.
          shouldRevalidate: ({
            currentParams,
            nextParams,
            currentUrl,
            nextUrl
          }: ShouldRevalidateFunctionArgs) =>
            currentParams.id !== nextParams.id || currentUrl.search !== nextUrl.search,
          loader: routeLoader(userGuards.passPlanHolder, ({ params, request }: LoaderContext) => {
            const id = parseInt(params!.id ?? '')
            if (isNaN(id) || id <= 0) return null
            // O fit desta página é o da oferta que a PDV exibiu. Sem `offerId` (educação,
            // resposta legada ou deep link cru) o backend devolve a oferta de maior
            // compatibilidade, como antes.
            const offerId = Number(new URL(request!.url).searchParams.get('offerId')) || null
            return Loaders.loadContentDetails(id, false, offerId)
          })
        },
        {
          path: PathConstants.Routes.USER_ENROLLMENT_SCHOLARSHIP_RENEWAL.value,
          element: <Pages.UserEnrollmentScholarshipRenewalPage />,
          loader: routeLoader(null, null)
        }
      ]
    },

    // ========================================
    // ROTAS DE DESCOBERTA DE CONTEÚDO
    // Layout: LoggedLayout (navMenu="main", footerVariant="full", enableSearch=true)
    // ========================================
    {
      element: <LoggedLayout navMenu="main" footerVariant="full" enableSearch={true} />,
      children: [
        {
          path: PathConstants.Routes.SEARCH.value,
          id: PathConstants.Routes.SEARCH.name,
          element: <Pages.SearchPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account
            })
          )
        },
        {
          path: PathConstants.Routes.SKILLS.value,
          id: PathConstants.Routes.SKILLS.name,
          element: <Pages.SkillSearchPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request, params }: LoaderContext) => {
            const userSkillId = params!.id ? parseInt(params!.id) : undefined
            return Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              userSkillId
            })
          })
        },
        {
          path: PathConstants.Routes.INSTITUTIONS.value,
          id: PathConstants.Routes.INSTITUTIONS.name,
          element: <Pages.InstitutionsListPartnersPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadInstitutionsSearch(new URL(request!.url).searchParams)
          )
        },
        {
          path: PathConstants.Routes.INSTITUTION.value,
          id: PathConstants.Routes.INSTITUTION.name,
          element: <Pages.InstitutionSearchPartnerPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request, params }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              institutionId: params?.id ? parseInt(params.id) : undefined
            })
          )
        },
        ...Category.getAvailables().map(category => ({
          path: PathConstants.getCategoryRoutePath(category),
          id: category.name.toLocaleLowerCase(),
          element: <Pages.CategorySearchPage category={category} />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              categories: [category]
            })
          )
        })),
        ...EnrollmentCategory.getGroups().map(group => ({
          path: PathConstants.getCategoryGroupRoutePath(group),
          id: group.name.toLocaleLowerCase(),
          element: <Pages.CategoryGroupSearchPage group={group} />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadContentSearch({
              searchParams: new URL(request!.url).searchParams,
              account: account,
              categories: group.categories
            })
          )
        })),
        {
          path: PathConstants.Routes.CONTENT.value,
          id: PathConstants.Routes.CONTENT.name,
          element: <Pages.ContentPage />,
          shouldRevalidate: ({ currentParams, nextParams }: ShouldRevalidateFunctionArgs) =>
            currentParams.id !== nextParams.id,
          loader: routeLoader(userGuards.passPlanHolder, ({ params }: LoaderContext) => {
            const id = parseInt(params!.id ?? '')
            if (isNaN(id) || id <= 0) return null
            return Loaders.loadContentDetails(id)
          })
        },
        {
          path: PathConstants.Routes.REFUND.value,
          element: <Pages.RefundProgramListPage />,
          loader: routeLoader(userGuards.passPlanHolder, () => Loaders.loadRefundProgramList())
        }
      ]
    },

    // ========================================
    // ROTAS DE AÇÃO
    // Layout: BasicLayout, footer compacto
    // Rotas para ações específicas do usuário (inscrição, solicitações, avaliações, etc.)
    // ========================================
    {
      element: <BasicLayout footerVariant="compact" />,
      children: [
        {
          // Gateada por flag NO GUARD, não só no menu: esconder o item de menu
          // deixaria a URL acessível a quem a digitasse.
          path: PathConstants.Routes.USER_LEARNING_PREFERENCES.value,
          element: <Pages.UserLearningPreferencesPage />,
          loader: routeLoader(
            userGuards.passPlanHolderWithFeature(FeatureFlagName.ENABLE_USER_LEARNING_PREFERENCES),
            () => Loaders.loadUserLearningPreferences()
          )
        },
        {
          path: PathConstants.Routes.CONTENT_ENROLLMENT.value,
          element: <Pages.ContentEnrollmentPage />,
          // Blocking: loadContentEnrollment may return a redirect Response,
          // which must resolve before navigation commits (cannot be deferred).
          loader: routeLoader(
            userGuards.passPlanHolder,
            ({ params }: LoaderContext) => Loaders.loadContentEnrollment(parseInt(params!.id!)),
            { blocking: true }
          )
        },
        {
          path: PathConstants.Routes.CONTENT_ENROLLMENT_SCHOLARSHIP_INFO.value,
          element: <Pages.ScholarshipRenewalInfoPage />
        },
        {
          path: PathConstants.Routes.CONTENT_UNSUBSCRIBE.value,
          element: <Pages.ContentUnsubscribePage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadContentUnsubscribe(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.CONTENT_COMPLETE.value,
          element: <Pages.ContentCompletePage />,
          loader: routeLoader(null, ({ params }: LoaderContext) =>
            Loaders.loadContentCompletion(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.CBU_START.value,
          element: (
            <RecaptchaProvider>
              <Pages.CbUnicoStartPage />
            </RecaptchaProvider>
          ),
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_CALLBACK.value,
          element: <Pages.ProfileAssessmentCallbackPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_IN_PROGRESS.value,
          element: <Pages.ProfileAssessmentInProgressPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.PROFILE_ASSESSMENT_GET_STARTED.value,
          element: <Pages.ProfileAssessmentGetStartedPage />,
          loader: routeLoader(null, null)
        },
        {
          path: PathConstants.Routes.REFUND_REQUEST.value,
          element: <Pages.RefundRequestPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadRefundRequest(params!.id!)
          )
        },
        {
          path: PathConstants.Routes.UPGRADE_REQUEST.value,
          element: <Pages.UpgradeRequestPage />,
          loader: routeLoader(userGuards.passPlanHolder, () => Loaders.loadUpgradeRequest(account))
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST.value,
          element: <Pages.DependentRequestPage />,
          loader: routeLoader(userGuards.passPlanHolder, () =>
            Loaders.loadDependentRequest(account)
          )
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST_CATALOG.value,
          element: <Pages.DependentRequestCatalogPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ request }: LoaderContext) =>
            Loaders.loadDependentCatalog(request!, account)
          )
        },
        {
          path: PathConstants.Routes.DEPENDENT_REQUEST_CONTENT.value,
          id: PathConstants.Routes.DEPENDENT_REQUEST_CONTENT.name,
          element: <Pages.DependentContentPage />,
          loader: routeLoader(userGuards.passPlanHolder, ({ params }: LoaderContext) =>
            Loaders.loadDependentContent(parseInt(params!.id!))
          )
        },
        {
          path: PathConstants.Routes.CONTACT_VERIFICATION.value,
          element: <Pages.ContactVerificationPage />,
          loader: routeLoader(null, () => Loaders.contactVerificationLoader())
        },
        {
          path: PathConstants.Routes.NO_PLAN.value,
          element: <Pages.NoPlanPage />,
          loader: routeLoader(null, null)
        }
      ]
    }
  ]
}
